import { Route, Routes } from 'react-router-dom';

import { RequireAuth, RequirePermission, RequireSuperadmin } from '@/auth/guards';
import { PERMISSIONS } from '@/auth/permissions';
import { AppShell } from '@/components/layout/AppShell';
import { LoginPage } from '@/features/auth/LoginPage';
import { SignupPage } from '@/features/auth/SignupPage';
import { VerifyEmailPage } from '@/features/auth/VerifyEmailPage';
import { ForgotPasswordPage } from '@/features/auth/ForgotPasswordPage';
import { StorePage } from '@/features/store/StorePage';
import { StoreOrderStatusPage } from '@/features/store/StoreOrderStatusPage';
import { PrivacyPolicyPage } from '@/features/legal/PrivacyPolicyPage';
import { AccountDeletionPage } from '@/features/legal/AccountDeletionPage';
import { BillingPage } from '@/features/billing/BillingPage';
import { InventoryPage } from '@/features/inventory/InventoryPage';
import { SalesHistoryPage } from '@/features/sales/SalesHistoryPage';
import { SuppliersPage } from '@/features/suppliers/SuppliersPage';
import { PurchaseOrdersPage } from '@/features/purchasing/PurchaseOrdersPage';
import { CustomersPage } from '@/features/customers/CustomersPage';
import { ReportsPage } from '@/features/reports/ReportsPage';
import { StaffPage } from '@/features/staff/StaffPage';
import { SettingsPage } from '@/features/settings/SettingsPage';
import { SubscriptionPage } from '@/features/subscription/SubscriptionPage';
import { DashboardPage } from '@/features/dashboard/DashboardPage';
import { SuperadminPage } from '@/features/superadmin/SuperadminPage';
import { AuditLogPage } from '@/features/audit/AuditLogPage';
import { NotFoundPage } from '@/components/layout/NotFoundPage';

/**
 * Route table. Each area is gated on the same permission key the API enforces,
 * so a user never lands on a screen whose every request would be refused.
 */
export default function App() {
  return (
    <Routes>
      {/* Public */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />

      {/* Patient storefront — the whole point is that it needs no staff
          login. A pharmacy shares /store/{slug} as a link (WhatsApp, a
          printed QR code, a bill footer); a patient never lands on /login.
          The order-status route's shape is fixed by the backend, which
          builds Cashfree's return_url as exactly this path. */}
      <Route path="/store/order/:orderId/status" element={<StoreOrderStatusPage />} />
      <Route path="/store/:pharmacySlug" element={<StorePage />} />

      {/* Play Store / App Store submission requirements — must be public,
          reachable without the app installed. See Settings > Account >
          "Delete my account" on mobile for the in-app counterpart. */}
      <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
      <Route path="/account-deletion" element={<AccountDeletionPage />} />

      {/* Authenticated shell */}
      <Route
        element={
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        }
      >
        <Route index element={<DashboardPage />} />

        <Route
          path="billing"
          element={
            <RequirePermission permission={PERMISSIONS.salesCreate}>
              <BillingPage />
            </RequirePermission>
          }
        />
        <Route
          path="inventory"
          element={
            <RequirePermission permission={PERMISSIONS.medicinesView}>
              <InventoryPage />
            </RequirePermission>
          }
        />
        <Route
          path="sales"
          element={
            <RequirePermission permission={PERMISSIONS.salesView}>
              <SalesHistoryPage />
            </RequirePermission>
          }
        />
        <Route
          path="purchases"
          element={
            <RequirePermission permission={PERMISSIONS.purchasesView}>
              <PurchaseOrdersPage />
            </RequirePermission>
          }
        />
        <Route
          path="suppliers"
          element={
            <RequirePermission permission={PERMISSIONS.suppliersView}>
              <SuppliersPage />
            </RequirePermission>
          }
        />
        <Route
          path="customers"
          element={
            <RequirePermission permission={PERMISSIONS.customersView}>
              <CustomersPage />
            </RequirePermission>
          }
        />
        <Route
          path="reports"
          element={
            <RequirePermission permission={PERMISSIONS.reportsSales}>
              <ReportsPage />
            </RequirePermission>
          }
        />
        <Route
          path="users"
          element={
            <RequirePermission permission={PERMISSIONS.usersView}>
              <StaffPage />
            </RequirePermission>
          }
        />
        {/* No permission gate here on purpose: password change and the counter
            UPI QR are personal, available to every signed-in user. Store and
            alert settings are gated internally, section by section. */}
        <Route path="settings" element={<SettingsPage />} />

        <Route
          path="audit-log"
          element={
            <RequirePermission permission={PERMISSIONS.auditView}>
              <AuditLogPage />
            </RequirePermission>
          }
        />

        {/* Always reachable — a lapsed pharmacy has to be able to renew. */}
        <Route path="subscription" element={<SubscriptionPage />} />

        <Route
          path="superadmin/*"
          element={
            <RequireSuperadmin>
              <SuperadminPage />
            </RequireSuperadmin>
          }
        />
      </Route>

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}